import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { useLanguage } from '../contexts/LanguageContext';
import { Calendar, User, Clock } from 'lucide-react';
import NewsModal from './NewsModal';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface NewsItem {
  id: number;
  image: string;
  title: string;
  description: string;
  category: string;
  date: string;
  author: string;
  readTime: string;
  'source-data': string;
}

export function TechNews() {
  const { data, language, isRTL } = useLanguage();
  const newsData = data.technews;
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState<{ id: number; sourceData: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!newsData.active) return null;

  useEffect(() => {
    loadNewsData();
  }, [newsData]);

  const loadNewsData = async () => {
    if (newsData['data-source']) {
      try {
        const response = await fetch(`/data/${newsData['data-source']}`);
        const data = await response.json();
        setNewsItems(data);
      } catch (error) {
        console.error('Error loading news data:', error);
      } finally {
        setLoading(false);
      }
    } else if (newsData.nodes) {
      // Fallback to old format
      setNewsItems(newsData.nodes);
      setLoading(false);
    }
  };

  const handleReadMore = (newsItem: NewsItem) => {
    setSelectedNews({ id: newsItem.id, sourceData: newsItem['source-data'] });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (language === 'ar') {
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section id="technews" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-16">{newsData.title}</h2>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 6000 }}
          dir={isRTL ? 'rtl' : 'ltr'}
          key={isRTL ? 'rtl' : 'ltr'}
          breakpoints={{
            640: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 3,
            },
          }}
          className="pb-12"
        >
          {loading ? (
            <SwiperSlide>
              <div className="bg-gray-50 rounded-xl p-6 animate-pulse">
                <div className="h-48 bg-gray-300 rounded mb-4"></div>
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 rounded mb-4"></div>
                <div className="h-8 bg-gray-300 rounded"></div>
              </div>
            </SwiperSlide>
          ) : newsItems.map((news) => (
            <SwiperSlide key={news.id}>
              <article className="bg-gray-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="relative">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {news.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-3 line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{news.description}</p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(news.date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{news.author}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{news.readTime}</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handleReadMore(news)}
                    className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    {language === 'ar' ? 'اقرأ المزيد' : 'Read More'}
                  </button>
                </div>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* News Modal */}
      <NewsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        newsId={selectedNews?.id || null}
        sourceData={selectedNews?.sourceData || null}
      />
    </section>
  );
}